import {useEffect, useState} from 'react';
import {getAwpApiBase} from '../utils/awpApiBase';
import {getAwpClientHeaders} from '../session/awpRequestHeaders';
import {isLinkedEntityRef} from '../utils/entityLinks';

const LOG_PREFIX = '[useEntity]';

/**
 * Fetch a single entity by its KB entity ID.
 * Phase 6 replacement for Firestore `entities/{entityId}` lookup.
 */
export function useEntity(entityId, refetchKey = 0) {
    const [entity, setEntity] = useState(null);
    const [loading, setLoading] = useState(!!entityId);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [resolved, setResolved] = useState(false);

    useEffect(() => {
        if (!entityId) {
            setEntity(null); setLoading(false); setError(null); setNotFound(false); setResolved(false);
            return;
        }

        // Product/FINTEL UUIDs and other non-KB refs must not hit /api/entities (HTTP 400).
        if (!isLinkedEntityRef(entityId)) {
            setEntity(null);
            setLoading(false);
            setError(null);
            setNotFound(true);
            setResolved(true);
            return;
        }

        let cancelled = false;
        setLoading(true); setError(null); setNotFound(false); setResolved(false); setEntity(null);
        console.debug(`${LOG_PREFIX} Fetching entity: ${entityId} (refetchKey=${refetchKey})`);

        fetch(`${getAwpApiBase()}/api/entities/${encodeURIComponent(entityId)}/summary`, {
            headers: getAwpClientHeaders(),
        })
            .then((r) => r.status === 404 ? null : r.ok ? r.json() : Promise.reject(new Error(`${r.status}`)))
            .then((data) => {
                if (cancelled) return;
                console.debug(`${LOG_PREFIX} Entity ${entityId}: ${data ? 'found' : 'not found'}`);
                setEntity(data ? {id: entityId, ...data} : null);
                setNotFound(!data);
                setResolved(true);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(`${LOG_PREFIX} Error:`, err);
                setError(err); setNotFound(false); setResolved(true); setLoading(false);
            });

        return () => { cancelled = true; };
    }, [entityId, refetchKey]);

    return {entity, loading, error, notFound, resolved};
}
